import AutorenewIcon from '@mui/icons-material/Autorenew'
import { IconButton, Tooltip } from '@mui/material'
import React, { useState } from 'react'
import styled from 'styled-components'

import {
	useAppContext,
	useAppDispatch,
	useDebounce,
	useEffectWithoutMount,
} from '@/hooks'
import {
	callNamingWorkingData,
	CallNamingWorkingDataParams,
} from '@/store/modules/table/utils/callNaming'
import { FormValue } from '@/types'
import { formatCodeByCase } from '@/utils'
import { logError } from '@/utils/errorHandling'

import { withFormField } from '../../FormFieldContext'
import { FormlessTextStyled } from '../../styles'
import { NamingProps } from './types'
import { useCanGenerateNaming } from './useCanGenerateNaming'

export const NamingInputFormless = ({
	id,
	node,
	callNamingWorkingDataParams,
	name,
	value,
	onChange,
	error,
	onFocus,
	onBlur,
	onFocusCustom,
	onBlurCustom,
	autoFocus,
	isReadOnly,
	readonlyContainer,
	disabled,
	updateNaming = true,
}: NamingProps) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const debouncedParams = useDebounce(callNamingWorkingDataParams, 200)
	const { generateElementCode, isUpperCase } = useCanGenerateNaming(node)
	const [isGenerating, setIsGenerating] = useState(false)

	useEffectWithoutMount(() => {
		if (updateNaming && generateElementCode && debouncedParams && !disabled) {
			const naming = dispatch(
				callNamingWorkingData(debouncedParams as CallNamingWorkingDataParams),
			)

			naming.then((naming) => {
				const result = naming?.result

				if (result !== value) {
					const parsedResult = formatCodeByCase(result, isUpperCase)
					onChange?.(parsedResult)
				}
			})
		}
	}, [updateNaming, generateElementCode, debouncedParams, isUpperCase, disabled])

	const handleChange = (value: FormValue) => {
		onChange?.(value)
	}

	const handleGenerateCode = async () => {
		if (!callNamingWorkingDataParams || disabled) return

		setIsGenerating(true)
		try {
			const naming = await dispatch(
				callNamingWorkingData(
					callNamingWorkingDataParams as CallNamingWorkingDataParams,
				),
			)

			const result = naming?.result
			if (result) {
				const parsedResult = formatCodeByCase(result, isUpperCase)
				onChange?.(parsedResult)
			}
		} catch (error) {
			logError(error, { operation: 'NamingInput.handleGenerateCode' })
		} finally {
			setIsGenerating(false)
		}
	}

	const renderFormlessTextStyled = () =>
		readonlyContainer ? (
			readonlyContainer(value)
		) : (
			<FormlessTextStyled value={value} disabled />
		)

	const showGenerateButton = !generateElementCode && !isReadOnly && !disabled

	return (
		<>
			{isReadOnly ? (
				renderFormlessTextStyled()
			) : (
				<InputContainer>
					<FormlessTextStyled
						id={id}
						name={name}
						value={value}
						onFocus={(e: React.FocusEvent<Element>) => {
							onFocus?.(e)
							onFocusCustom?.()
						}}
						onBlur={(e: React.FocusEvent<Element>) => {
							onBlur?.(e)
							onBlurCustom?.()
						}}
						error={error}
						onChange={handleChange}
						autoFocus={autoFocus}
						disabled={disabled || generateElementCode}
					/>
					{showGenerateButton && (
						<GenerateButtonWrapper>
							<Tooltip title={t('GENERATE_CODE_FROM_NAME')}>
								<span>
									<IconButton
										size="small"
										onClick={handleGenerateCode}
										disabled={!callNamingWorkingDataParams || isGenerating}
										data-testid={`naming-input-button-generate-${name}`}
									>
										<AutorenewIcon
											fontSize="small"
											className={isGenerating ? 'animate-spin' : ''}
										/>
									</IconButton>
								</span>
							</Tooltip>
						</GenerateButtonWrapper>
					)}
				</InputContainer>
			)}
		</>
	)
}

const InputContainer = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
`

const GenerateButtonWrapper = styled.div`
	margin-left: -32px;
	z-index: 1;
`

export const NamingInput = withFormField(NamingInputFormless)
